import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./contexts/AuthContext";

// Route Guards
import PublicRoute from "./components/guards/PublicRoute";
import RequireAuth from "./components/guards/RequireAuth";
import RootRedirect from "./components/guards/RootRedirect";

// Layouts
import Layout from "./components/layouts/TenantLayout";
import AdminLayout from "./components/layouts/AdminLayout";

// Shared Components (always loaded)
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorBoundary from "./components/ErrorBoundary";

import "./index.css";

// Lazy-loaded Tenant Pages
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const MagicLinkVerify = React.lazy(() => import("./pages/MagicLinkVerify"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Campaigns = React.lazy(() => import("./pages/Campaigns"));
const Reports = React.lazy(() => import("./pages/Reports"));
const SocialAccounts = React.lazy(() => import("./pages/SocialAccounts"));
const OAuthCallback = React.lazy(() => import("./pages/OAuthCallback"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Pricing = React.lazy(() => import("./pages/Pricing"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const Billing = React.lazy(() => import("./pages/Billing"));
const Subscribe = React.lazy(() => import("./pages/Subscribe"));

// Lazy-loaded Platform Admin Pages
const AdminLogin = React.lazy(() => import("./pages/auth/AdminLogin"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const UserManagement = React.lazy(() => import("./pages/admin/TenantManagement"));
const RevenueDashboard = React.lazy(() => import("./pages/admin/RevenueDashboard"));
const SocialConnectionsAdmin = React.lazy(() => import("./pages/admin/SocialConnectionsAdmin"));
const CustomerSubscriptions = React.lazy(() => import("./pages/admin/CustomerSubscriptions"));
const SystemLogs = React.lazy(() => import("./pages/admin/SystemLogs"));
const AdminSettings = React.lazy(() => import("./pages/admin/AdminSettings"));
const SupportTickets = React.lazy(() => import("./pages/admin/SupportTickets"));

// Google OAuth Client ID from environment
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// 404 fallback for unmatched routes
const NotFound = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="text-center">
      <div className="text-6xl mb-4">🔍</div>
      <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
      <p className="text-gray-600 mb-4">Page not found</p>
      <a href="/" className="text-blue-600 hover:text-blue-800 underline">
        Return to Home
      </a>
    </div>
  </div>
);

function App() {
  // Conditionally render GoogleOAuthProvider only if client ID is configured
  const AppContent = (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
            {/* Root redirect based on authentication state and tier */}
            <Route path="/" element={<RootRedirect />} />

            {/* Public Tenant Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute tier="tenant">
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute tier="tenant">
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/verify"
              element={
                <PublicRoute tier="tenant">
                  <MagicLinkVerify />
                </PublicRoute>
              }
            />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

              {/* Public Platform Admin Routes */}
              <Route
                path="/admin/login"
                element={
                  <PublicRoute tier="platform">
                    <AdminLogin />
                  </PublicRoute>
                }
              />

              {/* Protected Tenant Routes */}
              <Route
                path="/dashboard"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/campaigns"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Campaigns />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/reports"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Reports />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/social-accounts"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <SocialAccounts />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/oauth/callback"
                element={
                  <RequireAuth tier="tenant">
                    <OAuthCallback />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Settings />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/billing"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Billing />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/subscribe"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Subscribe />
                    </Layout>
                  </RequireAuth>
                }
              />

              {/* Protected Platform Admin Routes */}
              <Route
                path="/admin/dashboard"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <UserManagement />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/revenue"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <RevenueDashboard />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/social"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <SocialConnectionsAdmin />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <CustomerSubscriptions />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/logs"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <SystemLogs />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <AdminSettings />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <SupportTickets />
                    </AdminLayout>
                  </RequireAuth>
                }
              />

              {/* Legacy admin route redirects for backward compatibility */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/platforms" element={<Navigate to="/admin/social" replace />} />
              <Route path="/admin/billing" element={<Navigate to="/admin/revenue" replace />} />

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );

  // Only wrap with GoogleOAuthProvider if client ID exists
  return GOOGLE_CLIENT_ID ? (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {AppContent}
    </GoogleOAuthProvider>
  ) : (
    AppContent
  );
}

export default App;
